import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { IconAlert, IconCheckCircle, IconInfo } from './icons';

/* --------------------------------------------------------------- Button */

export function Button({
  children,
  variant = 'outline',
  size = 'md',
  loading = false,
  block = false,
  icon,
  className = '',
  disabled,
  ...rest
}: {
  children?: ReactNode;
  variant?: 'primary' | 'accent' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  block?: boolean;
  icon?: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const classes = [
    'btn',
    `btn-${variant}`,
    size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '',
    block ? 'btn-block' : '',
    children === undefined ? 'btn-icon' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="spinner" /> : icon}
      {children}
    </button>
  );
}

/* ----------------------------------------------------------------- Chip */

export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'critical';

export function Chip({
  children,
  tone = 'neutral',
  icon,
}: {
  children: ReactNode;
  tone?: Tone;
  icon?: ReactNode;
}) {
  return (
    <span className={`chip chip-${tone}`}>
      {icon}
      {children}
    </span>
  );
}

/**
 * A dot plus a word. Linear's discipline: state is carried by a small mark, so a
 * table of forty rows does not become forty coloured pills.
 */
export function StatusIndicator({
  tone = 'neutral',
  children,
}: {
  tone?: Tone;
  children: ReactNode;
}) {
  const dotTone = tone === 'critical' ? 'warning' : tone;
  return (
    <span className="status">
      <span className={`dot dot-${dotTone}`} aria-hidden="true" />
      <span className="status-label">{children}</span>
    </span>
  );
}

/* --------------------------------------------------------------- Notice */

export function Notice({
  tone = 'info',
  children,
  role,
}: {
  tone?: 'info' | 'warning' | 'critical';
  children: ReactNode;
  role?: 'alert' | 'status';
}) {
  const Icon = tone === 'info' ? IconInfo : IconAlert;
  return (
    <div className={`notice notice-${tone}`} role={role}>
      <Icon size={16} />
      <div>{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------- Skeleton */

/** Shaped like the content that is coming, never a generic spinner for a page. */
export function Skeleton({
  height = 16,
  width = '100%',
  radius,
}: {
  height?: number | string;
  width?: number | string;
  radius?: number;
}) {
  return (
    <div
      className="skeleton"
      style={{ height, width, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

/* ----------------------------------------------------------- EmptyState */

export function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon?: ReactNode;
  title: string;
  text?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon && <span className="empty-icon">{icon}</span>}
      <p className="empty-title">{title}</p>
      {text && <p className="empty-text">{text}</p>}
      {action && <div className="empty-action">{action}</div>}
    </div>
  );
}

/* --------------------------------------------------------------- Toasts */

export interface ToastMessage {
  id: number;
  text: string;
  tone: 'default' | 'success' | 'critical';
}

export function ToastRegion({ toasts }: { toasts: ToastMessage[] }) {
  return (
    <div className="toast-region no-print" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast-${toast.tone}`}>
          {toast.tone === 'success' && <IconCheckCircle size={15} />}
          {toast.tone === 'critical' && <IconAlert size={15} />}
          <span>{toast.text}</span>
        </div>
      ))}
    </div>
  );
}
